import { useState } from 'react';

interface EscrowExplainerProps {
  contractAddress?: string;
  variant?: 'seller' | 'buyer';
}

export function EscrowExplainer({ contractAddress, variant = 'buyer' }: EscrowExplainerProps) {
  const [expanded, setExpanded] = useState(false);

  const sellerSteps = [
    'You lock your assets in a smart contract — not Cherry\'s wallet.',
    'Buyers make offers with their own locked assets.',
    'When you accept, the contract swaps everything in one transaction.',
    'If you cancel, your assets are returned automatically.',
  ];

  const buyerSteps = [
    'You lock your assets in a smart contract — not Cherry\'s wallet.',
    'The seller reviews and can accept, reject, or counter.',
    'When both sides are ready, the contract swaps everything in one transaction.',
    'If the deal falls through, you get your assets back.',
  ];

  const steps = variant === 'seller' ? sellerSteps : buyerSteps;

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-text-secondary hover:bg-surface-alt transition-colors"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4 text-status-open" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          How Escrow Protects You
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <ol className="space-y-2 text-sm text-text-secondary list-decimal list-inside">
            {steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          {contractAddress && (
            <div className="flex items-center gap-2 pt-2 border-t border-border text-xs text-text-muted">
              <span>Contract:</span>
              <code className="bg-surface-alt px-1.5 py-0.5 rounded">
                {contractAddress.slice(0, 8)}...{contractAddress.slice(-6)}
              </code>
              <button
                onClick={() => void navigator.clipboard.writeText(contractAddress)}
                className="text-ton-500 hover:text-ton-600"
                aria-label="Copy contract address"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <a
                href={`https://tonscan.org/address/${contractAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ton-500 hover:text-ton-600"
                aria-label="View contract on explorer"
              >
                ↗
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
